const baseUrl = process.env.REACT_APP_BASE_URL || '/';

// Generic request handler
const request = async (endpoint, options = {}) => {
	try {
		const url = `${baseUrl}api/${endpoint}`;
		const response = await fetch(url, options);
		const data = await response.json();
		return data;
	} catch (error) {
		console.error(`API error (${endpoint}):`, error);
		return { error: 'Network error. Please check your connection.' };
	}
};

// Auth API
export const authAPI = {
	login: (username, password) => {
		return request('api/login', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ username, password }),
		});
	},

	logout: () => request('api/logout'),
};

// Admin API
export const adminAPI = {
	// Admin users
	getAdmins: () => request('admins/'),
	getAdmin: (id) => request(`admins/${id}`),
	createAdmin: (data) => {
		return request('admins/create', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		});
	},
	updateAdmin: (id, data) => {
		return request(`admins/update/${id}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		});
	},
	deleteAdmin: (id) => {
		return request(`admins/delete/${id}`, {
			method: 'DELETE',
		});
	},

	// Teachers
	getTeachers: () => request('teachers/'),
	getTeacher: (id) => request(`teachers/${id}`),
	createTeacher: (data) => {
		return request('teachers/create', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		});
	},
	updateTeacher: (id, data) => {
		return request(`teachers/update/${id}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		});
	},
	deleteTeacher: (id) => {
		return request(`teachers/delete/${id}`, {
			method: 'DELETE',
		});
	},

	// Moderation profiles
	getModerationProfiles: () => request('moderation_profiles/'),
	getModerationProfile: (id) => request(`moderation_profiles/${id}`),
	createModerationProfile: (formData) => {
		return request('moderation_profiles/create', {
			method: 'POST',
			body: formData, // FormData for multipart/form-data
		});
	},
	updateModerationProfile: (id, formData) => {
		return request(`moderation_profiles/update/${id}`, {
			method: 'PUT',
			body: formData, // FormData for multipart/form-data
		});
	},
	deleteModerationProfile: (id) => {
		return request(`moderation_profiles/delete/${id}`, {
			method: 'DELETE',
		});
	},
	deleteFile: (profileId, filename) => {
		return request(`delete/${profileId}/${filename}`, {
			method: 'POST',
		});
	},

	// Prompts
	getPrompts: () => request('prompts/'),
	getPrompt: (id) => request(`prompts/${id}`),
	createPrompt: (data) => {
		return request('prompts/', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		});
	},
	updatePrompt: (id, data) => {
		return request(`prompts/${id}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		});
	},
	deletePrompt: (id) => {
		return request(`prompts/${id}`, {
			method: 'DELETE',
		});
	},

	// Models
	getModelList: () => request('get_model_list'),
};

// Dialogues API
export const dialoguesAPI = {
	getDialogues: () => request('dialogues/'),
	getDialogue: (id) => request(`dialogues/${id}`),
	getDialogueGroups: (id) => request(`dialogues/${id}/groups`),
	createDialogue: (formData) => {
		return request('dialogues/create', {
			method: 'POST',
			body: formData, // FormData for multipart/form-data
		});
	},
	updateDialogue: (id, formData) => {
		return request(`dialogues/update/${id}`, {
			method: 'PUT',
			body: formData, // FormData for multipart/form-data
		});
	},
	deleteDialogue: (id) => {
		return request(`dialogues/delete/${id}`, {
			method: 'DELETE',
		});
	},
	endDialogue: (id) => request(`dialogues/end/${id}`),
	requestDialogueOutput: (id) => request(`dialogues/dialogue_output/${id}`),
	requestGroupOutput: (id) => request(`dialogues/group_output/${id}`),
	deleteDialogueFile: (dialogueId, filename) => {
		return request(`delete/dialogues/${dialogueId}/${filename}`, {
			method: 'POST',
		});
	},
};

// Chat API
export const chatAPI = {
	joinSession: (sessionId) =>
		request('api/chat', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				session_id: sessionId,
			}),
		}),
	submitDetails: (sessionId, details) => {
		const queryParams = new URLSearchParams({
			session_id: sessionId,
			...details,
		});
		return request(`api/chat?${queryParams.toString()}`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				session_id: sessionId,
				...details,
			}),
		});
	},
	requestOutput: (sessionId) =>
		request(`dialogues/output?session_id=${sessionId}`),
};
